/**
 * InsufficientSparksModal
 *
 * Hard-stop modal shown when a node-execute request returns HTTP 402
 * `insufficient_sparks`. Replaces the previous chat-side
 * `__insufficient_funds__` question for User-path clicks — the chat
 * was easy to miss and made users think the Generate button was
 * broken (they'd click 6 times → 6 cancel toasts).
 *
 * Informational only. There is no self-serve top-up (T276405916) — credits
 * are added by an operator via /admin/topup — so the modal explains the gap
 * and directs the user to request more, with a single dismiss action.
 *
 * The modal is mounted at the workspace level (`WorkspacePageV2`) and
 * driven by a context-provided `showInsufficientSparks(detail)` setter
 * that node-level Generate buttons call from their executeNode catch
 * blocks.
 */

import { useTranslation } from 'react-i18next'
import { useOptionalProjectId } from '../../contexts/ProjectIdContext'
import Modal from './Modal'
import { Zap } from 'lucide-react'
import { projectOwnerProps, trackInteraction } from '../../analytics/interactionAnalytics'
import { classifyBlockedAction } from '../../contexts/InsufficientSparksContext'

interface InsufficientSparksModalProps {
  isOpen: boolean
  onClose: () => void
  /** Current spark balance reported by the backend at the moment the
   *  gate tripped. Always >= 0. */
  balance?: number
  /** Sparks required for the run that was blocked. */
  required?: number
  /** Node type of the blocked run (analytics only). */
  nodeType?: string
}

export default function InsufficientSparksModal({
  isOpen,
  onClose,
  nodeType,
}: InsufficientSparksModalProps) {
  const { t } = useTranslation('shared')
  const projectId = useOptionalProjectId()

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('insufficientSparksModal.title')} size="sm">
      <div className="flex flex-col gap-4" data-testid="insufficient-sparks-modal">
        <div className="flex items-start gap-3">
          <span aria-hidden className="mt-0.5 inline-flex h-6 w-6 items-center justify-center text-warning">
            <Zap className="h-5 w-5" />
          </span>
          <div className="flex-1">
            <p className="text-sm text-foreground">
              {t('insufficientSparksModal.genericMessage')}
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={() => {
              trackInteraction('canvas_insufficient_credits_modal_action', {
                action: 'close',
                block_reason: 'unknown',
                blocked_action: classifyBlockedAction(nodeType),
                project_id: projectId ?? undefined,
                ...projectOwnerProps(projectId),
              })
              onClose()
            }}
            className="px-3 py-1.5 text-sm font-medium rounded border bg-primary text-primary-foreground border-primary hover:bg-primary/90 transition-colors"
            data-testid="insufficient-sparks-cancel"
          >
            {t('insufficientSparksModal.close')}
          </button>
        </div>
      </div>
    </Modal>
  )
}
